import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import { BookingGroupService } from 'us-web-services';
import { Button, ButtonType } from '../Button';
import { Modal } from '../Modal';

// Angular source: apps/angular/src/js/ng/parent/bookings/modals/sitters-needed-modal.*
// Count-picker that updates the booking group's numberOfSitters, then continues
// to the add-details step.
interface Props {
  isOpen: boolean;
  onClose: () => void;
  group: { id?: number };
  groupBookings: Array<{ caregiver?: { pictureUrl?: string } }>;
  sitter?: { pictureUrl?: string };
  onContinue: () => void;
}

const NUM_SITTERS = [
  { value: 2, text: '2' },
  { value: 3, text: '3' },
  { value: 4, text: '4' },
  { value: 5, text: '5' },
];

export const SittersNeededModal = ({
  isOpen,
  onClose,
  group,
  groupBookings,
  sitter,
  onContinue,
}: Props): JSX.Element => {
  const [count, setCount] = useState<number>(NUM_SITTERS[0].value);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setCount(NUM_SITTERS[0].value);
  }, [isOpen]);

  const save = async (): Promise<void> => {
    if (!group.id) return;
    setSubmitting(true);
    try {
      const resp = await BookingGroupService.patch(group.id, {
        id: group.id,
        numberOfSitters: count,
        status: 'pending',
      } as never);
      const updated = resp.data?.data as { numberOfSitters?: number } | undefined;
      if (Number(updated?.numberOfSitters) === Number(count)) {
        onContinue();
      }
    } catch {
      toast.error("Couldn't update. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className='w-full max-w-lg'>
      <Button
        aria-label='Close'
        onClick={onClose}
        className='absolute right-2 top-2 text-gray-400 hover:text-gray-500 transition-colors'
      >
        <FontAwesomeIcon icon={faXmark} className='w-5 h-5' />
      </Button>
      <div className='font-semibold mb-3'>Multiple caregivers</div>
      <h2 className='mb-3'>How many caregivers do you need?</h2>
      <div className='flex gap-2 mb-4 items-center'>
        {groupBookings[0]?.caregiver?.pictureUrl && (
          <img
            src={groupBookings[0].caregiver.pictureUrl}
            alt=''
            className='w-20 h-20 rounded object-cover'
          />
        )}
        {sitter?.pictureUrl && (
          <img
            src={sitter.pictureUrl}
            alt=''
            className='w-20 h-20 rounded object-cover'
          />
        )}
        <span className='bg-sapphire text-white text-4xl rounded w-20 h-20 flex items-center justify-center font-semibold'>
          ?
        </span>
      </div>
      <div className='mb-4'>
        <select
          id='num_sitters'
          className='form-control w-40 border border-neutral-300 rounded px-3 py-2'
          value={count}
          onChange={(e): void => setCount(Number(e.target.value))}
        >
          {NUM_SITTERS.map((n) => (
            <option key={n.value} value={n.value}>
              {n.text}
            </option>
          ))}
        </select>
      </div>
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
        <Button
          id='update-sitters-needed'
          buttonType={ButtonType.primary}
          className='w-full'
          disabled={submitting}
          onClick={save}
        >
          Continue
        </Button>
        <Button
          id='cancel-multi-sitters'
          buttonType={ButtonType.secondary}
          className='w-full'
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </Modal>
  );
};
